"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { EmailInput } from "@/components/ui";

export default function NightlifeHero() {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");

  const handleSignup = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      return;
    }
    setLoading(true);
    try {
      await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "nightlife", lang }),
      });
      setDone(true);
      setEmail("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative pt-28 pb-24 px-6 overflow-hidden bg-[#0A0814] text-white">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/solution-nightlife.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay — video shows through at ~25% */}
      <div className="absolute inset-0 bg-[#0A0814]/75 pointer-events-none" />

      {/* Background glows — violet + magenta */}
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#7C3AED]/25 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#F472B6]/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-[#4A7AE8]/12 rounded-full blur-[120px] pointer-events-none" />

      {/* Grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur-xl px-4 py-2 rounded-full text-xs font-bold text-white shadow-sm">
            <Sparkles className="w-3 h-3 text-[#F472B6] fill-[#F472B6]" />
            {t("Built for Bars, Clubs & Live Events", "Hecho para Bares, Clubs y Eventos")}
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center text-[40px] sm:text-5xl md:text-[72px] font-black leading-[1.02] tracking-tight mb-6 max-w-[900px] mx-auto"
        >
          <span className="text-white">
            {t("Ticketing. Door. Bar. VIP.", "Tickets. Puerta. Barra. VIP.")}
          </span>
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
            }}
          >
            {t("One platform.", "Una plataforma.")}
          </span>
        </motion.h1>

        {/* Subhead */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center text-lg md:text-xl text-white/60 max-w-[640px] mx-auto leading-relaxed mb-10 font-medium"
        >
          {t(
            "Stop juggling a ticketing site, a door app, a bar POS, a spreadsheet for promoters, and a website for events. Redy Nightlife runs all of it from one screen.",
            "Deja de malabarear un sitio de tickets, una app de puerta, un POS de barra, una hoja de promotores y un sitio de eventos. Redy Nightlife corre todo desde una sola pantalla."
          )}
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-col items-center gap-4"
        >
          {done ? (
            <div className="flex items-center gap-3 bg-[#7C3AED]/15 border border-[#7C3AED]/30 rounded-full px-6 py-4 max-w-[500px]">
              <CheckCircle2 className="w-5 h-5 text-[#C4B5FD] shrink-0" />
              <span className="text-[15px] font-bold text-white">
                {t("Check your inbox — we'll be in touch.", "Revisa tu inbox — te escribimos en breve.")}
              </span>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-[500px]">
              <EmailInput
                value={email}
                onChange={setEmail}
                placeholder={t("your@venue.com", "tu@venue.com")}
                disabled={loading}
                inputClassName="flex-1 px-6 py-4 bg-white/5 border border-white/15 rounded-full text-base font-medium text-white placeholder:text-white/40 outline-none focus:border-[#C4B5FD] focus:ring-2 focus:ring-[#7C3AED]/30 transition-all backdrop-blur-xl disabled:opacity-60"
              />
              <button
                onClick={handleSignup}
                disabled={loading}
                className="group px-8 py-4 font-bold rounded-full shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed text-white"
                style={{
                  backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
                }}
              >
                {loading ? t("Sending…", "Enviando…") : t("Get a demo", "Pide un demo")}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </div>
          )}

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-2">
            <div className="flex items-center gap-2 text-[13px] font-bold text-white/55">
              <CheckCircle2 className="w-4 h-4 text-[#C4B5FD] shrink-0" />
              {t("No platform fee per ticket", "Sin fee de plataforma por ticket")}
            </div>
            <div className="flex items-center gap-2 text-[13px] font-bold text-white/55">
              <CheckCircle2 className="w-4 h-4 text-[#C4B5FD] shrink-0" />
              {t("Realtime door + bar", "Puerta y barra en vivo")}
            </div>
            <div className="flex items-center gap-2 text-[13px] font-bold text-white/55">
              <CheckCircle2 className="w-4 h-4 text-[#C4B5FD] shrink-0" />
              {t("Promoter links on PRO", "Links de promotores en PRO")}
            </div>
          </div>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-[820px] mx-auto"
        >
          {[
            { value: "1", label: t("Platform for the whole venue", "Plataforma para todo el venue") },
            { value: "$0", label: t("Platform fee per ticket", "Fee de plataforma por ticket") },
            { value: t("Realtime", "En vivo"), label: t("Door, bar and VIP map", "Puerta, barra y mapa VIP") },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-[24px] px-6 py-5 text-center"
            >
              <div className="text-[36px] font-black text-white leading-none mb-1">{stat.value}</div>
              <div className="text-[12px] font-bold text-white/55">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
